import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AgentIcon from '../../components/picture-ai/AgentIcon';
import ProgramsSummary from '../../components/picture-ai/ProgramsSummary';
import { agentPresentation } from './agents';
import { buttonClass, cardClass, formatCost, usePictureAiApi } from './usePictureAi';

/**
 * The agents this deployment can run, and what is running on each.
 *
 * Agents are code, not records: the list cannot be edited here. What varies is how many
 * configurations point at each one and which programs they cover.
 */
const AgentMarketplace = ({ fetchWithAuth }) => {
  const { t } = useTranslation();
  const request = usePictureAiApi(fetchWithAuth);
  const [agents, setAgents] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    request('/admin/agents')
      .then(setAgents)
      .catch(caught => setError(caught.message))
      .finally(() => setLoading(false));
  }, [request]);

  if (loading) return <p className="text-sm text-gray-500">{t('Loading…')}</p>;
  if (error) return <p className="rounded-lg bg-red-50 p-4 text-sm text-red-800 dark:bg-red-950/40 dark:text-red-200">{error}</p>;

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t('AI agents')}</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {t('Each agent is a specialised analysis. Assign one to programs by creating a configuration.')}
        </p>
      </header>

      <div className="grid gap-4 lg:grid-cols-2">
        {agents.map((agent) => {
          const shown = agentPresentation(agent.key);
          return (
          <article key={agent.key} className={`${cardClass} overflow-hidden`} style={{ borderTopWidth: 3, borderTopColor: shown.color }}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <AgentIcon agent={shown} size={44} />
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t(shown.name)}</h2>
                  <p className="text-sm" style={{ color: shown.color }}>{t(shown.tagline)}</p>
                  <code className="text-xs text-gray-400">{agent.key}</code>
                </div>
              </div>
              <Link className={buttonClass} to={`/admin/ai/configurations/new?agent=${agent.key}`}>
                {t('Create a configuration')}
              </Link>
            </div>

            <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">{t(shown.description)}</p>

            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div>
                <div className="text-xs font-semibold uppercase text-gray-500">{t('What it returns')}</div>
                <ul className="mt-1 list-inside list-disc text-xs leading-5 text-gray-600 dark:text-gray-300">
                  {shown.outputs.map(item => <li key={item}>{t(item)}</li>)}
                </ul>
              </div>
              <div>
                <div className="text-xs font-semibold uppercase text-gray-500">{t('Use it for')}</div>
                <ul className="mt-1 list-inside list-disc text-xs leading-5 text-gray-600 dark:text-gray-300">
                  {shown.goodFor.map(item => <li key={item}>{t(item)}</li>)}
                </ul>
                {shown.notFor.length > 0 && (
                  <p className="mt-2 text-xs italic text-gray-500 dark:text-gray-400">{shown.notFor.map(item => t(item)).join(' · ')}</p>
                )}
              </div>
            </div>

            <dl className="mt-4 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
              <div>
                <dt className="text-xs uppercase text-gray-500">{t('Configurations')}</dt>
                <dd className="font-semibold text-gray-900 dark:text-white">{agent.configurations}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase text-gray-500">{t('Enabled')}</dt>
                <dd className="font-semibold text-gray-900 dark:text-white">{agent.enabled_configurations}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase text-gray-500">{t('Analyzed pictures')}</dt>
                <dd className="font-semibold text-gray-900 dark:text-white">{agent.analysed_pictures}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase text-gray-500">{t('Cost')}</dt>
                <dd className="font-semibold text-gray-900 dark:text-white">{formatCost(agent.cost_micros)}</dd>
              </div>
            </dl>

            <div className="mt-4">
              <div className="text-xs uppercase text-gray-500">{t('Programs running it')}</div>
              <div className="mt-1">
                <ProgramsSummary
                  projectkeys={agent.projectkeys}
                  hiddenCount={agent.hidden_programs_count}
                  request={request}
                  title={t('Programs running it')}
                />
              </div>
            </div>

            <div className="mt-3 flex flex-wrap gap-1 text-xs text-gray-400">
              {agent.variants.map(variant => <code key={variant} className="rounded bg-gray-100 px-1.5 py-0.5 dark:bg-gray-800">{variant}</code>)}
            </div>
          </article>
          );
        })}
      </div>
    </div>
  );
};

export default AgentMarketplace;
